import React, { useState, useEffect, useContext, createContext } from 'react';
import { useStaticQuery, graphql, Link } from 'gatsby';

import useWindowSize from '../../hooks/useWindowSize';
import { ButtonSmall } from '../../ui/Button';
import Wrapper from '../../ui/Wrappers';
import { BREAKPOINTS, SUB_MENU_HEIGHT } from '../../ui/settings';
import { externalLink, unitlessSpace } from '../../ui/helpers';
import Chevron from '-!svg-react-loader!../../ui/svgs/icon-chevron.svg';
import FooterImage from '../../images/KIDSANDUS_RGB.png';
import FooterImagergb from '../../images/KIDSANDUS_RGB_BLACK.png';

import {
  Container,
  CTAContainer,
  CTAContainerFooter,
  Dropdown,
  Hamburger,
  LogoContainer,
  Nav,
  Primary,
  Secondary,
  SecondaryContainer,
  StyledLink
} from './styles';

type ContextType = {
  context: Array<number>;
  setContext: (value: Array<number>) => void;
};

const MenuContext = createContext<ContextType>({
  context: [0, 0, 0],
  setContext: () => {}
});

const MenuIn = (items: any[], lang, url) => {
  let menuPath = [];
  for (let i = 0; items && i < items.length; ++i) {
    if (items[i].childrenMenuItems) {
      menuPath = MenuIn(items[i].childrenMenuItems, lang, url);
    }

    if (menuPath.length > 0) {
      menuPath.unshift(items[i]);
      return menuPath;
    }

    if (decodeURIComponent(items[i].url) === decodeURIComponent(`${url}`)) {
      menuPath.unshift(items[i]);
      return menuPath;
    }
  }

  return menuPath;
};

const MenuElement = props => {
  const vpSize = useWindowSize();
  const { context, setContext } = useContext(MenuContext);
  const [active, setActive] = useState(false);

  useEffect(() => {
    vpSize.width < BREAKPOINTS.medium ? props?.current && setActive(true) : setActive(false);
  }, [vpSize]);

  useEffect(() => {
    if (active && props.identifier && context) {
      if (context[0] !== props.identifier[0]) setActive(false);
      else if (
        context[0] === props.identifier[0] &&
        props.identifier[1] > 0 &&
        context[1] !== props.identifier[1]
      )
        setActive(false);
    }
  }, [context]);

  const classes = [];
  classes.push(active ? 'active' : '');
  classes.push(props.children ? 'root' : '');
  classes.push(props.last ? 'last' : '');
  classes.push(props?.current ? 'current' : '');
  classes.push(props.primary ? 'primary' : '');
  const optionClass = props?.options?.attributes?.class ? props.options.attributes.class.join(" ") : undefined;
  const optionTarget = props?.options?.attributes?.target ? props.options.attributes.target : undefined;
  return (
    <StyledLink className={classes.join(' ').trim()}>
      {props.children ? (
        <button
          onClick={() =>
            vpSize.width < BREAKPOINTS.medium
              ? (setActive(!active), setContext(props.identifier))
              : setActive(false)
          }
          className={`${props?.current ? 'current' : ''}`}>
          {props.children && props.last && (
            <Chevron className={`chevron ${active ? 'active' : ''}`} />
          )}
          <span>{props.label}</span>
          {props.children && !props.last && (
            <Chevron className={`chevron ${active ? 'active' : ''}`} />
          )}
        </button>
      ) : (externalLink(props.to) || optionTarget === "_blank") ? (
        <a
          href={props.to}
          target={optionTarget ? optionTarget : '_blank'}
          className={`${props?.current ? 'current' : optionClass ? optionClass : ''}`}
          data-gtm-event
          data-gtm-label={props.to}>
          <span>{props.label}</span>
        </a>
      ) : (
        <Link
          to={props.to}
          data-gtm-event
          data-gtm-label={props.to}
          target= {optionTarget ? optionTarget : '_self'}
          className={`${props?.current ? 'current' : optionClass ? optionClass : ''}`}>
          <span>{props.label}</span>
        </Link>
      )}
      {props.children}
    </StyledLink>
  );
};

const Navigation = ({
  data,
  langcode,
  mainName,
  secondName,
  pageCta,
  domain,
  pageHref,
  isFranchise,
  translations
}: any) => {
  const [context, setContext] = useState<Array<number>>([0, 0, 0]);
  const [open, setOpen] = useState(false);
  const [menuBreakpoint, setMenuBreakpoint] = useState(false);
  const [sticky, setSticky] = useState(false);
  const vpSize = useWindowSize();

  const logoSchema = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    'url': `${domain}`,
    'logo': `${domain}`
  };

  const mappedPrimary = [];
  const mappedSecondary = [];

  let jsxPrimaryMenu = null;
  let jsxSecondaryMenu = null;
  let timer: any;
  let windowWidth: number = 0;

  const handleMenuToggle = () => {
    setOpen(!open);
  };

  const updateHeaderState = () => {
    const menuBreakpoint = window.matchMedia(`(min-width: ${BREAKPOINTS.medium}px)`).matches;
    const startPoint = menuBreakpoint ? SUB_MENU_HEIGHT + unitlessSpace(2) : 0;
    setMenuBreakpoint(menuBreakpoint);
    setSticky(window.scrollY > startPoint);
  };

  useEffect(() => {
    document.documentElement.classList[open ? 'add' : 'remove']('menu-open');
  }, [open]);

  useEffect(() => {
    windowWidth = window.innerWidth;

    const onResizeEvent = () => {
      if (windowWidth !== window.innerWidth) {
        windowWidth = window.innerWidth;

        if (timer) clearTimeout(timer);

        timer = setTimeout(updateHeaderState, 100);
      }
    };

    updateHeaderState();
    window.addEventListener('resize', onResizeEvent);
    window.addEventListener('scroll', updateHeaderState);

    return () => {
      window.removeEventListener('resize', onResizeEvent);
      window.removeEventListener('scroll', updateHeaderState);
    };
  }, []);

  data.allMenuItems.edges.map(value => {
    if (
      value.node.langcode === langcode &&
      value.node.menu_name === mainName &&
      value.node.enabled
    ) {
      if (value.node.title == 'Demo day' && (langcode == 'eu' || langcode == 'gl' || langcode == 'ca' || langcode == 'en-be' || langcode == 'nl-be') && !value.node.url.includes('/' + langcode)) {
        let originalUrl = value.node.url;
        let firstPart = originalUrl.split('/demo-day')[0]

        let finalUrl = firstPart + '/' + value.node.langcode + '/demo-day'

        value.node.url = finalUrl
      }
      mappedPrimary.push(value.node);
    } else if (
      value.node.langcode === langcode &&
      value.node.menu_name === secondName &&
      value.node.enabled
    ) {
      mappedSecondary.push(value.node);
    }
  });

  let menuPathArray = MenuIn(mappedPrimary, langcode, pageHref);

  const createPrimaryMenu = () => {
    return (
      <ul className="links">
        {mappedPrimary.map((parent, i) => (
          <MenuElement
            key={'kParent_' + i}
            to={parent.url}
            label={parent.title}
            identifier={[i + 1, 0, 0]}
            primary={true}
            options={parent.options}
            current={menuPathArray.some(e => e.title === parent.title) ? true : null}>
            {parent.childrenMenuItems && parent.childrenMenuItems.length > 0 && (
              <Dropdown
                className={
                  i === mappedPrimary.length - 1 &&
                  vpSize.width > BREAKPOINTS.medium &&
                  !pageCta?.internal.title
                    ? 'primary-last'
                    : ''
                }>
                <ul>
                  {parent.childrenMenuItems.map((child, j) => (
                    <MenuElement
                      key={'kChild_' + i + '_' + j}
                      to={child.url}
                      label={child.title}
                      identifier={[i + 1, j + 1, 0]}
                      current={menuPathArray.some(e => e.title === child.title) ? true : null}
                      last={
                        i === mappedPrimary.length - 1 && vpSize.width > BREAKPOINTS.medium
                          ? true
                          : false
                      }>
                      {child.childrenMenuItems && child.childrenMenuItems.length > 0 && (
                        <Dropdown
                          className={`second-level ${
                            i === mappedPrimary.length - 1 ? 'last' : ''
                          }`}>
                          <ul>
                            {child.childrenMenuItems.map((grandChildren, k) => (
                              <MenuElement
                                key={'kGChild_' + i + '_' + j + '_' + k}
                                to={grandChildren.url}
                                label={grandChildren.title}
                                identifier={[i + 1, j + 1, k + 1]}
                                current={
                                  menuPathArray.some(e => e.title === grandChildren.title)
                                    ? true
                                    : null
                                }
                              />
                            ))}
                          </ul>
                        </Dropdown>
                      )}
                    </MenuElement>
                  ))}
                </ul>
              </Dropdown>
            )}
          </MenuElement>
        ))}
      </ul>
    );
  };

  !jsxPrimaryMenu && mappedPrimary.length > 0 && (jsxPrimaryMenu = createPrimaryMenu());

  const createSecondaryMenu = () => {
    return (
      <ul className="links">
        {mappedSecondary.map((secondary, i) => (
          <MenuElement
            key={'kSecond_' + i}
            to={secondary.url}
            label={secondary.title}
          />
        ))}
      </ul>
    );
  };

  !jsxSecondaryMenu && mappedSecondary.length > 0 && (jsxSecondaryMenu = createSecondaryMenu());

  const SecondaryWrapper = ({ className, children }) =>
    className === 'for-desktop' ? <Wrapper fullwidth>{children}</Wrapper> : <>{children}</>;

  const SecondaryMenu = props =>
    jsxSecondaryMenu !== null ? (
      <SecondaryContainer {...props}>
        <SecondaryWrapper {...props}>
          <Secondary data-gtm-action="header" data-gtm-category="navegacion">
            {jsxSecondaryMenu}
          </Secondary>
        </SecondaryWrapper>
      </SecondaryContainer>
    ) : (
      <></>
    );

  const isSSR = typeof window === "undefined";
  const footerContactButtonClassName = 'footer-contact-button';

  if (!isSSR) {
    const hubspotButtonObserver = new MutationObserver(function () {
      const element = document.querySelector('#hubspot-messages-iframe-container');
      const footerContactButton = document.querySelector(`.${footerContactButtonClassName}`);

      if (document.body.contains(element)) {
        footerContactButton?.classList.add('has-hubspot-button');
      } else {
        footerContactButton?.classList.remove('has-hubspot-button');
      }
    });

    hubspotButtonObserver.observe(document.body, { subtree: false, childList: true });
  }

  return (
    <Container sticky={sticky}>
      <SecondaryMenu className="for-desktop" />
      <div className="sticky">
        <Wrapper fullwidth className="wrapper">
          <LogoContainer sticky={sticky}>
            <Link
              to={`/${langcode}`}
              style={{width:'max-content'}}
              aria-label={`This image is the Kids and Us logo. Clicking on this image will redirect you to the home`}>
              <img src={FooterImage} className="headLogo-bw"></img>
              <img src={FooterImagergb} className="headLogo-c"></img>
            </Link>
          </LogoContainer>
          <MenuContext.Provider value={{ context, setContext }}>
            <Nav className={`${open ? 'open' : ''} ${sticky ? 'is-sticky' : ''}`}>
              <Primary data-gtm-action="menu" data-gtm-category="navegacion">
                {jsxPrimaryMenu !== null && jsxPrimaryMenu}
              </Primary>
              <SecondaryMenu className="for-mobile" />
              {(pageCta?.ctaRef || (pageCta?.external && pageCta?.internal.title)) && (
                <CTAContainer
                  className={footerContactButtonClassName}
                >
                  {pageCta.ctaRef ? (
                    <ButtonSmall
                      className="cta"
                      $full={vpSize.width < BREAKPOINTS.medium}
                      onClick={() =>
                        window.scrollTo({
                          top: pageCta.ctaRef.ref.current.offsetTop - 64,
                          behavior: 'smooth'
                        })
                      }>
                      {pageCta?.internal.title}
                    </ButtonSmall>
                  ) : (
                    <ButtonSmall
                      as={Link}
                      className={`cta ${isFranchise ? 'franchiseCta' : ''}`}
                      $full={vpSize.width < BREAKPOINTS.medium}
                      to={pageCta?.external}
                      $inverted={menuBreakpoint && !sticky}>
                      {pageCta?.internal.title}
                    </ButtonSmall>
                  )}
                </CTAContainer>
              )}
            </Nav>
            {(pageCta?.ctaRef || (pageCta?.external && pageCta?.internal.title)) && (
                <CTAContainerFooter
                  className={footerContactButtonClassName}
                >
                  {pageCta.ctaRef ? (
                    <ButtonSmall
                      className="cta"
                      $full={vpSize.width < BREAKPOINTS.medium}
                      onClick={() =>
                        window.scrollTo({
                          top: pageCta.ctaRef.ref.current.offsetTop - 64,
                          behavior: 'smooth'
                        })
                      }>
                      {pageCta?.internal.title}
                    </ButtonSmall>
                  ) : (
                    <ButtonSmall
                      as={Link}
                      className={`cta ${isFranchise ? 'franchiseCta' : ''}`}
                      $full={vpSize.width < BREAKPOINTS.medium}
                      to={pageCta?.external}
                      $inverted={menuBreakpoint && !sticky}>
                      {pageCta?.internal.title}
                    </ButtonSmall>
                  )}
                </CTAContainerFooter>
              )}
          </MenuContext.Provider>
          <Hamburger className={open ? 'open' : ''} onClick={handleMenuToggle} sticky={sticky}>
            <span />
            <div className="text">{translations.menu}</div>
          </Hamburger>
        </Wrapper>
      </div>

    </Container>
  );
};

interface IProps {
  mainName: string;
  secondName: string;
  langcode: any;
  pageCta: any;
  domain: string;
  isFranchise?: boolean;
  pageHref?: string;
  translations: any;
}

const NavigationMenu = ({
  mainName,
  secondName,
  langcode,
  pageCta,
  domain,
  pageHref,
  isFranchise,
  translations
}: IProps) => {
  const data = useStaticQuery(graphql`
    query NavMenuQuery {
      allMenuItems(filter: { parent: { id: { in: [null] } } }) {
        edges {
          node {
            id
            title
            url
            langcode
            enabled
            menu_name
            weight
            options{
              attributes{
                class
                target
              }
            }
            childrenMenuItems {
              id
              title
              url
              langcode
              enabled
              menu_name
              weight
              childrenMenuItems {
                id
                title
                url
                langcode
                enabled
                menu_name
                weight
                childrenMenuItems {
                  id
                  title
                  url
                  langcode
                  enabled
                  menu_name
                  weight
                }
              }
            }
          }
        }
      }
    }
  `);

  return (
    <Navigation
      data={data}
      langcode={langcode}
      mainName={mainName}
      secondName={secondName}
      pageCta={pageCta}
      domain={domain}
      pageHref={pageHref}
      isFranchise={isFranchise}
      translations={translations}
    />
  );
};

export default NavigationMenu;
